import {
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useState, useMemo } from "react";
import { ColorsConstants } from "../../cssUtils/constants";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import { formatDateOnly, formatWithTzLabel } from "../../utils/dateUtils";

// ─── Small reusable chip ──────────────────────────────────────────────────────

const TagChip = ({ label }) => (
  <Chip
    variant="outlined"
    label={label}
    size="small"
    color="primary"
    sx={{
      color: ColorsConstants.primaryBlue100,
      borderColor: ColorsConstants.primaryBlue100,
    }}
  />
);

const getLatestNameSegment = (value) => {
  if (typeof value !== "string") return value ?? "";
  const parts = value
    .split(" & ")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts[parts.length - 1] : "";
};

// ─── Component ────────────────────────────────────────────────────────────────

const AudienceTableComponent = ({
  hasDeletePermission,
  hasEditPermission,
  columns,           // original column definitions passed in from parent (used to detect "select" / "" ids)
  audienceData,
  rowCount,
  controller,
  tenant,
  tenantSpecificColumns,
  hasSelectedAll = false,
  handleSelectAll,
  handleSelectRecord,
  isRecordSelected,
  setSelectedEditAudience,
  setEditAudienceModalOpen,
  handleDeleteAudienceClick,
  handlePageChange,
  handleChangeRowsPerPage,
  audienceLoading,
}) => {
  const [activeTags, setActiveTags] = useState([]);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const reorderTags = (tags) => {
    if (!Array.isArray(tags)) return [];
    const priority = ["Interested", "Not Interested", "Requested Callback"];
    return [
      ...tags.filter((t) => priority.includes(t.name)),
      ...tags.filter((t) => !priority.includes(t.name)),
    ];
  };

  // ── Tag dialog handlers ────────────────────────────────────────────────────

  const handleTagsClick = (tags) => {
    setActiveTags(tags);
    setTagsDialogOpen(true);
  };

  const handleTagsDialogClose = () => {
    setTagsDialogOpen(false);
    setActiveTags([]);
  };

  // ── Derive flags from the incoming column list ─────────────────────────────
  // The parent passes `columns` to signal which structural columns are active.
  const hasSelectColumn  = columns.some((col) => col.id === "select");
  const hasActionsColumn = columns.some((col) => col.id === "");

  // ── Build AppTable column definitions ─────────────────────────────────────
  // Rebuilt only when the dependencies that affect column shape change.

  const tableColumns = useMemo(() => {
    const cols = [
      // Company
      { id: "company", label: "Company" },
      // Name
      {
        id: "name",
        label: "Contact Name",
        render: (audience) => {
          const displayName = getLatestNameSegment(audience.name) || "N/A";
          return (
            <Typography
              variant="body2"
              sx={{
                cursor: hasEditPermission ? "pointer" : "default",
                width: "fit-content",
                fontWeight: 600,
                color: hasEditPermission ? ColorsConstants.primaryBlue100 : "#1D2330",
                fontSize: "14px",
              }}
            >
              {displayName}
            </Typography>
          );
        },
      },
      // Phone
      {
        id: "phone_no",
        label: "Phone",
        render: (a) => `(+${a.country_code}) ${a.phone_no}`,
      },
      // DOB
      {
        id: "dob",
        label: "Date of Birth",
        render: (a) => formatDateOnly(a.dob_iso),
      },
      { id: "city",  label: "City" },
      { id: "state", label: "State" },
      // Tags — cell has its own click handler to open the dialog
      {
        id: "tags",
        label: "Tags",
        render: (audience) => (
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            onClick={(e) => {
              e.stopPropagation();
              handleTagsClick(audience.tags);
            }}
            sx={{ cursor: "pointer" }}
          >
            {Array.isArray(audience.tags) && audience.tags.length > 0 && (
              <>
                {reorderTags(audience.tags)
                  .slice(0, 1)
                  .map((tag, idx) => (
                    <TagChip key={idx} label={tag.name} />
                  ))}
                {audience.tags.length > 1 && (
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "13px", color: ColorsConstants.primaryBlue100 }}
                  >
                    +{audience.tags.length - 1}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        ),
      },
      // Tenant-specific dynamic columns
      ...(tenantSpecificColumns[tenant] ?? []).map((col) => ({
        id: col.id,
        label: col.label,
      })),
      // Opted-in
      {
        id: "opted_in",
        label: "Opted In",
        render: (a) =>
          a.opted_in === 1 ? (
            <Chip
              variant="outlined"
              color="success"
              label="Yes"
              size="small"
              icon={<CheckCircleIcon />}
              sx={{ fontSize: "11px", fontWeight: 600 }}
            />
          ) : a.opted_in === 0 ? (
            <Chip
              variant="outlined"
              color="error"
              label="No"
              size="small"
              icon={<CancelIcon />}
              sx={{ fontSize: "11px", fontWeight: 600 }}
            />
          ) : null,
      },
      { id: "updated_by", label: "Updated By" },
      {
        id: "updated_at",
        label: "Updated At",
        render: (audience) => formatWithTzLabel(audience.updated_at_iso),
      },
      // Conditional delete column — only when parent signals it and permission exists
      ...(hasActionsColumn && hasDeletePermission
        ? [
            {
              id: "__delete__",
              label: "Actions",
              align: "center",
              render: (audience) => (
                <Tooltip title="Delete audience" placement="top" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAudienceClick(audience);
                    }}
                    sx={{
                      color: "#1D2330",
                      borderRadius: "6px",
                      "&:hover": { bgcolor: "#FFF0F0", color: "#E05252" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ),
            },
          ]
        : []),
    ];

    return cols;
  }, [
    hasEditPermission,
    hasDeletePermission,
    hasActionsColumn,
    tenant,
    tenantSpecificColumns,
    handleDeleteAudienceClick,
  ]);

  // ── Row click — opens edit modal, guarded by permission ───────────────────
  const handleRowClick = hasEditPermission
    ? (audience) => {
        setSelectedEditAudience(audience);
        setEditAudienceModalOpen(true);
      }
    : undefined;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <AppTable
        columns={tableColumns}
        rows={audienceData}
        rowKey="id"
        loading={audienceLoading}
        emptyMessage="No match found for this phone number"
        maxHeight={600}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        // Row click (AppTable already guards against checkbox/button/icon targets)
        onRowClick={handleRowClick}
        rowClickable={hasEditPermission}
        // Selection
        selectable={hasSelectColumn}
        hasSelectedAll={hasSelectedAll}
        onSelectAll={handleSelectAll}
        isRowSelected={(row) => isRecordSelected(row.id)}
        onSelectRow={(event, row) => handleSelectRecord(event, row)}
        hideSelectAllWhenEmpty
        // Pagination
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Tags dialog — local state, unrelated to AppTable */}
      <Dialog
        open={tagsDialogOpen}
        onClose={handleTagsDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tags Applied</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {reorderTags(activeTags).map((tag, index) => (
              <TagChip key={index} label={tag.name} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <ButtonTwo title="Close" onClick={handleTagsDialogClose} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AudienceTableComponent;